import { formatDateOnly } from '../../../../lib/formatDate';
import {
  ADDRESS_KIND,
  COMPETENCY_LEVEL_LABELS,
  DISCIPLINARY_ACTION_TYPE_LABELS,
  DISCIPLINARY_CATEGORY_LABELS,
  DISCIPLINARY_STATUS_LABELS,
  EDUCATION_LEVEL_LABELS,
  EMPLOYMENT_TYPE_LABELS,
  type PersonnelDetail,
} from '../personnelTypes';

/**
 * Searching one 201 file across every section it holds.
 *
 * **It runs IN MEMORY, over the record the page already has.** The detail endpoint returns
 * every collection with the record, which is the same fact that lets each section's own table
 * search, sort and page client-side — so a search across all of them is reading what is
 * already on the page rather than a new door into the file. No endpoint, no second fetch, and
 * nothing a reader could not have found by opening the panels one at a time.
 *
 * **IT DOES NOT SEARCH NAMES, and that is the isolation boundary rather than an oversight.**
 * A family member, a referee and an emergency contact each carry a `PersonId` and no name;
 * the name lives in `rsys_pii` and is resolved by the PANEL that lists them, when that panel
 * opens. Making those names searchable here would mean resolving every one of them on every
 * profile open — a batch identity read nobody asked for, to answer a question nobody has yet.
 * Those rows ARE searched, on the facts stored beside them (a relation, an occupation, an
 * employer, a contact number), and the results panel says so in as many words.
 *
 * It does not search the employee's OWN name or date of birth either, for the same reason:
 * those are revealed deliberately, on the panel that shows them, and a search box is not a
 * reveal affordance.
 */

/** One matching row, with the reason it matched. */
export interface ProfileSearchHit {
  /** Stable within its group — the row's own id wherever there is one. */
  key: string;

  /**
   * What identifies the row inside its section — the same value its panel's anchor column
   * carries, so a hit is recognisable before the panel is opened.
   */
  title: string;

  /**
   * The fields that matched, in declaration order.
   *
   * The reason the hit is shown rather than the whole row: a result that printed every column
   * would be the panel again, and a result that printed only the title would not say why it
   * is there.
   */
  matches: { label: string; value: string }[];

  /**
   * The row is soft-deleted.
   *
   * Marked rather than hidden: every section arrives WHOLE, deleted rows and all, so
   * dropping them here would make the search disagree with the panel it sends the reader
   * to — and a reader hunting a row they remember deleting is exactly who needs it found.
   */
  deleted: boolean;
}

/** Every hit in one section, under the nav key that opens it. */
export interface ProfileSearchGroup {
  /** The `<ProfileNav>` key, so a result can send the reader to the section it came from. */
  panel: string;
  hits: ProfileSearchHit[];
}

/** A field offered to the search: its label, and what it reads off a row. */
type Field<T> = [label: string, read: (row: T) => string | number | null | undefined];

interface Section<T> {
  panel: string;
  rows: (record: PersonnelDetail) => readonly T[];
  title: (row: T) => string;
  fields: Field<T>[];
}

/** A date as the panels print it. Searching the ISO string would match nothing a reader types. */
function onDate(value: string | null | undefined): string | null {
  return value ? formatDateOnly(value) : null;
}

function section<T>(definition: Section<T>): Section<unknown> {
  return definition as unknown as Section<unknown>;
}

/*
  THE SECTIONS, in the rail's own order.

  A section added to the 201 file owes an entry here in the same change — the same rule the
  nav entry and `PERSONNEL_AUDIT_ENTITY_TYPES` already carry, and it fails the same way: the
  section is simply absent from the results and nothing says so.

  Only STORED values are listed. A derived figure (a SALN total, years of service) is left out
  deliberately: nobody searches for a number they have never been shown, and a hit on one would
  send the reader to a panel where the value they typed appears nowhere.

  AND EVERY SECTION HERE IS ONE `personnel.read` ALREADY BUYS. The three nav entries that carry
  a key of their own — Leave credits, Issued assets and Account — are deliberately absent, and
  they are not an oversight to correct: their rows do not arrive with the record, and a search
  that returned them would disclose under `personnel.read` what their own gates withhold. **A
  section added here must be checked against its nav entry's `permission` first.** Nothing in
  this file consults one, because today nothing in it needs to.
*/
const SECTIONS: Section<unknown>[] = [
  section<PersonnelDetail['addresses'][number]>({
    panel: 'address',
    rows: (record) => record.addresses,
    title: (row) => (row.kind === ADDRESS_KIND.permanent ? 'Permanent address' : 'Present address'),
    fields: [
      ['Street', (row) => row.streetAddress],
      ['Barangay', (row) => row.barangay],
      ['City / Municipality', (row) => row.cityMunicipality],
      ['Province', (row) => row.province],
      ['ZIP code', (row) => row.zipCode],
    ],
  }),

  section<PersonnelDetail['identifications'][number]>({
    panel: 'identification',
    rows: (record) => record.identifications,
    title: (row) => row.typeName,
    fields: [
      ['Type', (row) => row.typeName],
      ['Code', (row) => row.typeCode],
      ['Place issued', (row) => row.placeIssued],
      ['Date issued', (row) => onDate(row.dateIssued)],
      ['Valid until', (row) => onDate(row.validUntil)],

      // No NUMBER. It is in `rsys_pii` and reading one is an audited reveal, one card at a
      // time — a search that could confirm a number by guessing at it is that reveal, unaudited.
    ],
  }),

  section<PersonnelDetail['emergencyContacts'][number]>({
    panel: 'contacts',
    rows: (record) => record.emergencyContacts,
    title: (row) => row.relationship,
    fields: [
      ['Relationship', (row) => row.relationship],
      ['Contact number', (row) => row.contactNumber],
      ['Street', (row) => row.streetAddress],
      ['Barangay', (row) => row.barangay],
      ['City / Municipality', (row) => row.cityMunicipality],
      ['Province', (row) => row.province],
    ],
  }),

  section<PersonnelDetail['employmentRecords'][number]>({
    panel: 'employment',
    rows: (record) => record.employmentRecords,
    title: (row) => row.positionTitle ?? row.agencyName ?? row.itemNumber ?? 'Employment record',
    fields: [
      ['Position', (row) => row.positionTitle],
      ['Item number', (row) => row.itemNumber],
      ['Office / Unit', (row) => row.orgUnitName],
      ['Agency', (row) => row.agencyName],
      ['Salary grade', (row) => row.salaryGrade],
      ['Step', (row) => row.stepIncrement],
      ['Nature of appointment', (row) => row.appointmentNatureName],
      [
        'Status of appointment',
        (row) => (row.appointmentStatus ? EMPLOYMENT_TYPE_LABELS[row.appointmentStatus] : null),
      ],
      ['From', (row) => onDate(row.effectiveFrom)],
      ['To', (row) => onDate(row.effectiveTo)],
      ['Remarks', (row) => row.remarks],
    ],
  }),

  section<PersonnelDetail['familyMembers'][number]>({
    panel: 'family',
    rows: (record) => record.familyMembers,
    title: (row) => row.relationName,
    fields: [
      ['Relation', (row) => row.relationName],
      ['Occupation', (row) => row.occupation],
      ['Employer', (row) => row.employerName],
      ['Business address', (row) => row.businessAddress],
      ['Telephone', (row) => row.telephoneNumber],
    ],
  }),

  section<PersonnelDetail['educations'][number]>({
    panel: 'education',
    rows: (record) => record.educations,
    title: (row) => `${EDUCATION_LEVEL_LABELS[row.level]} — ${row.schoolName}`,
    fields: [
      ['Level', (row) => EDUCATION_LEVEL_LABELS[row.level]],
      ['School', (row) => row.schoolName],
      ['Degree or course', (row) => row.degreeOrCourse],
      ['Highest level or units', (row) => row.highestLevelUnitsEarned],
      ['Year graduated', (row) => row.yearGraduated],
      ['Honors', (row) => row.honors],
    ],
  }),

  section<PersonnelDetail['eligibilities'][number]>({
    panel: 'eligibility',
    rows: (record) => record.eligibilities,
    title: (row) => row.eligibilityName,
    fields: [
      ['Eligibility', (row) => row.eligibilityName],
      ['Rating', (row) => row.rating],
      ['Place of examination', (row) => row.placeOfExamination],
      ['Date of examination', (row) => onDate(row.dateOfExamination)],
      ['License number', (row) => row.licenseNumber],
      ['License valid until', (row) => onDate(row.licenseValidUntil)],
    ],
  }),

  section<PersonnelDetail['voluntaryWorks'][number]>({
    panel: 'voluntary-work',
    rows: (record) => record.voluntaryWorks,
    title: (row) => row.organizationName,
    fields: [
      ['Organization', (row) => row.organizationName],
      ['Address', (row) => row.organizationAddress],
      ['Position or nature of work', (row) => row.positionOrNature],
      ['From', (row) => onDate(row.periodFrom)],
      ['To', (row) => onDate(row.periodTo)],
    ],
  }),

  section<PersonnelDetail['trainings'][number]>({
    panel: 'training',
    rows: (record) => record.trainings,
    title: (row) => row.title,
    fields: [
      ['Title', (row) => row.title],
      ['Record number', (row) => row.recordNumber],
      ['Type of LD', (row) => row.categoryName],
      ['Type', (row) => row.typeName],
      ['Intervention', (row) => row.interventionName],
      ['Sponsor', (row) => row.sponsorName],
      ['Purpose', (row) => row.purpose],
      ['Competencies', (row) => row.competencies.map((c) => c.name).join(', ')],
      ['From', (row) => onDate(row.dateFrom)],
      ['To', (row) => onDate(row.dateTo)],
    ],
  }),

  section<PersonnelDetail['specialSkills'][number]>({
    panel: 'special-skills',
    rows: (record) => record.specialSkills,
    title: (row) => row.name,
    fields: [
      ['Skill', (row) => row.name],
      ['Category', (row) => row.categoryName],
      ['Proficiency', (row) => COMPETENCY_LEVEL_LABELS[row.proficiencyLevel]],
      ['Years of experience', (row) => row.yearsOfExperience],
      ['Remarks', (row) => row.remarks],
    ],
  }),

  section<PersonnelDetail['recognitions'][number]>({
    panel: 'recognition',
    rows: (record) => record.recognitions,
    title: (row) => row.title,
    fields: [
      ['Title', (row) => row.title],
      ['Awarded by', (row) => row.awardedBy],
      ['Date awarded', (row) => onDate(row.dateAwarded)],
      ['Description', (row) => row.description],
    ],
  }),

  section<PersonnelDetail['references'][number]>({
    panel: 'references',
    rows: (record) => record.references,
    title: (row) => row.company ?? row.occupation ?? 'Reference',
    fields: [
      ['Occupation', (row) => row.occupation],
      ['Company', (row) => row.company],
      ['Address', (row) => row.address],
      ['Contact number', (row) => row.contactNumber],
    ],
  }),

  section<PersonnelDetail['surveyAnswers'][number]>({
    panel: 'survey',
    rows: (record) => record.surveyAnswers,
    title: (row) => `${row.questionNumber} — ${row.questionText}`,
    fields: [
      ['Question', (row) => row.questionText],
      ['Answer', (row) => (row.answer ? 'Yes' : 'No')],
      ['Details', (row) => row.details],
      ['Status of the case', (row) => row.caseStatus],
      ['ID number', (row) => row.idNumber],
      ['Country', (row) => row.countryName],
      ['Date filed', (row) => onDate(row.dateFiled)],
    ],
  }),

  section<PersonnelDetail['salns'][number]>({
    panel: 'saln',
    rows: (record) => record.salns,
    title: (row) => `SALN ${row.asOfYear}`,
    fields: [
      ['As of year', (row) => row.asOfYear],
      ['Remarks', (row) => row.remarks],

      /*
        THE SCHEDULES ARE SEARCHED THROUGH THEIR PARENT, as one joined value per schedule.

        A line is not a record a reader navigates to — it has no row of its own on the panel,
        which lists filings — so a hit on one belongs to its filing. The label still names the
        schedule, or a hit would say a year matched and not which of four tables it came from.
      */
      ['Real properties', (row) => row.realProperties.map((p) => p.description).join(', ')],
      [
        'Personal properties',
        (row) => row.personalProperties.map((p) => p.description).join(', '),
      ],
      [
        'Liabilities',
        (row) => row.liabilities.map((l) => [l.nature, l.creditorName].filter(Boolean).join(' — ')).join(', '),
      ],
      [
        'Business interests',
        (row) => row.businessInterests.map((b) => b.entityName).join(', '),
      ],
    ],
  }),

  section<PersonnelDetail['disciplinaryRecords'][number]>({
    panel: 'disciplinary',
    rows: (record) => record.disciplinaryRecords,
    title: (row) => row.subject,
    fields: [
      ['Subject', (row) => row.subject],
      ['Case number', (row) => row.caseNumber],
      ['Type', (row) => DISCIPLINARY_ACTION_TYPE_LABELS[row.type]],
      ['Category', (row) => DISCIPLINARY_CATEGORY_LABELS[row.category]],
      ['Status', (row) => DISCIPLINARY_STATUS_LABELS[row.status]],
      ['Description', (row) => row.description],
      ['Action taken', (row) => row.actionTaken],
      ['Remarks', (row) => row.remarks],
      ['Incident date', (row) => onDate(row.incidentDate)],
      ['Date filed', (row) => onDate(row.dateFiled)],
      ['Resolution date', (row) => onDate(row.resolutionDate)],
    ],
  }),

  section<PersonnelDetail['documents'][number]>({
    panel: 'documents',
    rows: (record) => record.documents,
    title: (row) => row.fileName,
    fields: [
      ['File name', (row) => row.fileName],
      ['Document', (row) => row.documentTypeName],
    ],
  }),
];

/** The shortest term worth running. One character matches most of a file and says nothing. */
export const PROFILE_SEARCH_MINIMUM = 2;

/**
 * Every row of the record matching `term`, grouped by the section it came from.
 *
 * Case-insensitive substring, which is what the module's own tables do — a reader who types
 * `payroll` expects the rows a `LIKE` would return, not a ranked relevance list they cannot
 * predict. Groups keep `SECTIONS` order, hits keep the record's own, so the results read in
 * the order the rail and the panels do.
 *
 * A DELETED row is MARKED, not hidden — see `ProfileSearchHit.deleted`.
 */
export function searchPersonnelProfile(
  record: PersonnelDetail | null,
  term: string,
): ProfileSearchGroup[] {
  const needle = term.trim().toLowerCase();

  if (!record || needle.length < PROFILE_SEARCH_MINIMUM) {
    return [];
  }

  const groups: ProfileSearchGroup[] = [];

  for (const definition of SECTIONS) {
    const hits: ProfileSearchHit[] = [];

    definition.rows(record).forEach((row, index) => {
      const matches: { label: string; value: string }[] = [];

      for (const [label, read] of definition.fields) {
        const raw = read(row);
        if (raw === null || raw === undefined || raw === '') continue;

        const value = String(raw);
        if (value.toLowerCase().includes(needle)) {
          matches.push({ label, value });
        }
      }

      if (matches.length > 0) {
        // An address has no id of its own to key on and no IsActive — a row of the record
        // rather than a record of its own — so both fall back rather than being special-cased.
        const identified = row as { id?: string; isActive?: boolean };

        hits.push({
          key: identified.id ?? `${definition.panel}-${index}`,
          title: definition.title(row),
          matches,
          deleted: identified.isActive === false,
        });
      }
    });

    if (hits.length > 0) {
      groups.push({ panel: definition.panel, hits });
    }
  }

  return groups;
}

/** How many rows matched in all — what the search field's own count segment reports. */
export function countProfileSearchHits(groups: ProfileSearchGroup[]): number {
  return groups.reduce((total, group) => total + group.hits.length, 0);
}
